import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrg } from '../app/OrgContext.js';
import { C, alpha } from '../lib/theme.js';
import { Eyebrow } from '../ui/ui.js';
import { Icon, type IconName } from '../ui/icons.js';
import botIcon from '../assets/basil-bot.png';

// Hand a question/command to Basil Bot: it opens with the full conversation and
// answers, resuming the same chat (BasilBot listens for this event app-wide).
function askBasil(text: string) {
  const t = text.trim();
  if (t) window.dispatchEvent(new CustomEvent('basil:ask', { detail: t }));
}

const SUGGESTIONS = [
  'What should I do next?',
  'How are my ads doing?',
  'How do I launch an ad?',
  'Build a lookalike from my guests',
];

function AskBasilBox() {
  const [q, setQ] = useState('');
  const submit = () => {
    askBasil(q);
    setQ('');
  };
  return (
    <div
      style={{
        border: `1px solid ${C.line}`,
        borderRadius: 16,
        padding: 18,
        marginBottom: 26,
        background: C.surface,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <img
          src={botIcon}
          alt="Basil"
          style={{
            width: 30,
            height: 30,
            borderRadius: '50%',
            objectFit: 'cover',
            background: '#fff',
            border: `1.5px solid ${alpha(C.basil, 50)}`,
          }}
        />
        <div style={{ fontSize: 13.5, fontWeight: 600, color: C.primary }}>
          Ask Basil anything, or tell it what to do
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            }
          }}
          placeholder="e.g. What should I do to get more bookings this week?"
          style={{
            flex: 1,
            minWidth: 0,
            background: C.surface2,
            border: `1px solid ${C.line}`,
            borderRadius: 11,
            padding: '12px 14px',
            fontSize: 13.5,
            color: C.primary,
            fontFamily: 'inherit',
          }}
        />
        <button
          type="button"
          onClick={submit}
          disabled={!q.trim()}
          style={{
            padding: '0 18px',
            borderRadius: 11,
            border: 'none',
            background: C.basil,
            color: C.basilInk,
            fontSize: 13.5,
            fontWeight: 600,
            cursor: q.trim() ? 'pointer' : 'default',
            opacity: q.trim() ? 1 : 0.5,
            fontFamily: 'inherit',
          }}
        >
          Ask
        </button>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 11 }}>
        {SUGGESTIONS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => askBasil(s)}
            style={{
              padding: '6px 12px',
              borderRadius: 999,
              border: `1px solid ${C.line}`,
              background: C.surface2,
              color: C.secondary,
              fontSize: 12,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Home — the idiot-proof entry hub. One question, big signposted buttons that
 * drop you straight into the right tool. The account metrics that used to live
 * here now sit on Results (next to the booking bottom line).
 */

function greeting(): string {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
}

interface Action {
  icon: IconName;
  title: string;
  desc: string;
  to: string;
}

// The four things a user actually comes here to do, in pipeline order.
const PRIMARY: Action[] = [
  {
    icon: 'write',
    title: 'Compose a post',
    desc: 'Pick a visual (image or reel) and write the on-brand copy — then send to Launch.',
    to: '/studio/copy',
  },
  {
    icon: 'create',
    title: 'Style an image',
    desc: 'Optional: lay the brand type onto a static photo.',
    to: '/studio/create',
  },
  {
    icon: 'boost',
    title: 'Boost a post',
    desc: 'Put budget behind an existing post and take it live (always paused first).',
    to: '/launch?mode=amplify',
  },
  {
    icon: 'sparkles',
    title: 'Launch an ad',
    desc: 'Turn a Basil post (image + copy) straight into a fresh paid ad — full targeting, paused first.',
    to: '/launch?mode=new',
  },
  {
    icon: 'results',
    title: 'See your results',
    desc: 'Are your ads producing bookings? What to keep, scale, or kill.',
    to: '/results',
  },
];

// Everything else, one tap away.
const SECONDARY: Action[] = [
  {
    icon: 'library',
    title: 'Your Library',
    desc: 'Finished posts + photos & clips',
    to: '/studio/library',
  },
  { icon: 'tag', title: 'Tag bookings', desc: 'Attribute revenue to campaigns', to: '/bookings' },
  { icon: 'campaigns', title: 'Campaigns', desc: 'Every campaign, ad set & ad', to: '/campaigns' },
  { icon: 'settings', title: 'Settings', desc: 'Connections, team, offers', to: '/settings' },
];

export function Home() {
  const { me } = useOrg();
  const navigate = useNavigate();
  const dn = me?.profile?.display_name?.trim() ?? '';
  const name = dn && !dn.includes('@') ? dn : '';

  return (
    <div style={{ padding: '30px 30px 48px', maxWidth: 920, animation: 'basilfade .3s ease' }}>
      <Eyebrow style={{ marginBottom: 9 }}>
        {greeting()}
        {name ? `, ${name}` : ''}
      </Eyebrow>
      <h1 className="page-title" style={{ marginBottom: 20 }}>
        What do you want to do?
      </h1>

      <AskBasilBox />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 14,
          marginBottom: 30,
        }}
      >
        {PRIMARY.map((a) => (
          <button
            key={a.to}
            type="button"
            onClick={() => navigate(a.to)}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = C.basil;
              e.currentTarget.style.background = alpha(C.basil, 8);
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = C.line;
              e.currentTarget.style.background = C.surface;
            }}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 15,
              padding: '20px 22px',
              borderRadius: 16,
              cursor: 'pointer',
              fontFamily: 'inherit',
              textAlign: 'left',
              border: `1px solid ${C.line}`,
              background: C.surface,
              transition: 'border-color .18s ease, background .18s ease',
            }}
          >
            <span
              style={{
                width: 46,
                height: 46,
                flexShrink: 0,
                borderRadius: 12,
                background: alpha(C.basil, 12),
                color: C.basil,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon name={a.icon} size={22} />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  font: `700 16px 'Outfit',sans-serif`,
                  color: C.primary,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                {a.title}
                <span style={{ color: C.basil, fontSize: 15 }}>→</span>
              </div>
              <div style={{ fontSize: 12.5, color: C.muted, marginTop: 5, lineHeight: 1.5 }}>
                {a.desc}
              </div>
            </div>
          </button>
        ))}
      </div>

      <div className="label-mono" style={{ marginBottom: 12 }}>
        OR JUMP TO
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 10,
        }}
      >
        {SECONDARY.map((a) => (
          <button
            key={a.to}
            type="button"
            onClick={() => navigate(a.to)}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = C.lineStrong)}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = C.line)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '13px 15px',
              borderRadius: 11,
              cursor: 'pointer',
              fontFamily: 'inherit',
              textAlign: 'left',
              border: `1px solid ${C.line}`,
              background: C.surface,
              transition: 'border-color .18s ease',
            }}
          >
            <span
              style={{
                width: 22,
                flexShrink: 0,
                color: C.muted,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Icon name={a.icon} size={17} />
            </span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.secondary }}>{a.title}</div>
              <div style={{ fontSize: 11, color: C.faint, marginTop: 1 }}>{a.desc}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
